import type { SxProps, Theme } from '@mui/material';

/**
 * Paper styling for sheet-style dialogs (workout logger, exercise picker,
 * chart help sheets).
 *
 * global.css sizes every `.MuiDialog-paper` with `!important` (it caps width
 * to the viewport and, under 768px, forces `height: auto` plus an 8px margin).
 * That is right for ordinary pop-ups, but it stops MUI's `maxWidth` from
 * working on desktop and stops `fullScreen` from being full screen on a phone.
 * `&&` doubles the class specificity so these win without touching global.css.
 */
export function sheetPaperSx(fullScreen: boolean, desktopWidth: number) {
  return fullScreen
    ? {
        '&&': {
          margin: '0 !important',
          width: '100vw !important',
          maxWidth: '100vw !important',
          height: '100% !important',
          maxHeight: '100% !important',
          borderRadius: '0 !important',
          border: 'none !important',
        },
      }
    : {
        '&&': {
          width: `${desktopWidth}px`,
          borderRadius: '14px !important',
          border: '1px solid rgba(0,224,255,0.2) !important',
        },
      };
}

/**
 * Bottom sheet on phones, centred dialog on desktop — for short content such
 * as a chart's "how to read this" help. Pair it with
 * `bottomSheetDialogSx(phone)` on the Dialog itself, which pins the paper to
 * the bottom edge on phones.
 */
export function bottomSheetPaperSx(phone: boolean, desktopWidth: number) {
  return phone
    ? {
        '&&': {
          margin: '0 !important',
          width: '100vw !important',
          maxWidth: '100vw !important',
          maxHeight: 'calc(var(--vh, 1vh) * 85) !important',
          borderRadius: '18px 18px 0 0 !important',
          border: '1px solid rgba(0,224,255,0.2) !important',
          borderBottom: 'none !important',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        },
      }
    : sheetPaperSx(false, desktopWidth);
}

/** Dialog-level sx that anchors the paper to the bottom of the screen on phones. */
export function bottomSheetDialogSx(phone: boolean): SxProps<Theme> {
  return phone ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : {};
}
